import { Component, computed, EventEmitter, inject, input, Input, Output, Signal } from '@angular/core';
import { IconComponent } from '../../../../../../../../shared/components/ui/icon/icon.component';
import { TranslatePipe } from '../../../../../../../../shared/pipes/translate/translate.pipe';
import { CollectionFormField, Record } from 'src/models/ts/collection-form-field.model';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { LinkedCollectionStorageType } from '../../../../../../../../../models/ts/linked-collection-storage-type.model';
import { AuthService } from '../../../../../../../../core/services/auth/auth.service';
import { Claims } from '../../../../../../../../core/constants/claims';
import { UserType } from '../../../../../../../../../models/ts/user-type.model';
import { UpdateDeleteState } from '../../../../../../../../../models/ts/update-delete-state.model';
import {
  AllowDeletionLinkedRecordsType,
  CollectionFormExternalAccessDto
} from '../../../../../../../../../models/ts/collection-form-external-access-dto';
import { FormType } from '../../../../../../../../../models/ts/form-type.model';
import { Store } from '@ngrx/store';
import { StoreCollectionForm } from '../../../../../../../../store/features/forms/forms-state';

@Component({
  selector: 'bizz-from-field-grid-action-cell',
  standalone: true,
  templateUrl: './from-field-grid-action-cell.component.html',
  styleUrl: './from-field-grid-action-cell.component.scss',
  imports: [IconComponent, TranslatePipe, TooltipsModule]
})
export class FromFieldGridActionCellComponent {

  public record = input.required<Record>();
  @Input({ required: true }) public recordsSignal: Signal<Record[]>;
  @Input({ required: true }) public gridFieldSignal: Signal<CollectionFormField | undefined>;
  @Input() public externalAccess: CollectionFormExternalAccessDto | undefined;
  @Input() public formType?: FormType;
  @Input() public actionsDisabled: boolean;
  @Input() public readOnlyOverwrite: boolean;
  @Input({ required: true }) public formSignal: Signal<StoreCollectionForm | undefined>;

  public recordSignal = computed(() => this.recordsSignal().find(r => r.CrossLinkedInstancesID == this.record().CrossLinkedInstancesID));
  public userType = inject(AuthService).getAccessTokenClaim(Claims.UserType);
  public store$ = inject(Store);

  public canReadInStack = computed(() => {
    const gridField = this.gridFieldSignal();
    const record = this.recordSignal();

    if (gridField !== undefined && record !== undefined) {
      return this.userType != UserType.AnonymousUser && this.userType != UserType.ExternalUser && gridField.CanReadOnStack;
    } else return false;
  });

  public canEditInStack = computed(() => {
    const gridField = this.gridFieldSignal();
    const record = this.recordSignal();
    const form = this.formSignal();

    if (gridField !== undefined && record !== undefined && form !== undefined) {
      if (!gridField.AllowEditInStack)
        return false;
      const viewDataSource = form.data.ViewDataSources.find(vds => vds.ViewDataSourcesID == gridField.ViewDataSourcesID);
      if (viewDataSource !== undefined)
        return this.userType != UserType.AnonymousUser && this.userType != UserType.ExternalUser && gridField.CanEditOnStack && viewDataSource.LinkedCollectionStorageType != LinkedCollectionStorageType.Historical && !gridField.IsReadOnly && !record.EditMode && !this.readOnlyOverwrite && record.State != UpdateDeleteState.Update;
      else return false;
    } else return false;
  });

  public canEditInline = computed(() => {
    const gridField = this.gridFieldSignal();
    const record = this.recordSignal();
    if (gridField && record) {
      return this.externalUserCheck(this.externalAccess?.AllowCreateRecord && this.externalAccess?.AllowLookup) && !gridField.IsReadOnly && !this.readOnlyOverwrite && !record.EditMode;
    } else return false;
  });

  public canDelete = computed(() => {
    const gridField = this.gridFieldSignal();
    if (gridField) {
      return this.externalUserCheck(this.externalAccess?.AllowDeletionLinkedRecords == AllowDeletionLinkedRecordsType.Yes) && !gridField.IsReadOnly && !this.readOnlyOverwrite;
    } else return false;
  });

  public canSave = computed(() => {
    const gridField = this.gridFieldSignal();
    const record = this.recordSignal();
    if (gridField && record) {
      return !gridField.IsReadOnly && record.EditMode && !this.readOnlyOverwrite;
    } else return false;
  });

  @Output() public openClicked: EventEmitter<SelectedGridData> = new EventEmitter<SelectedGridData>();
  @Output() public editInNewWindowClicked: EventEmitter<SelectedGridData> = new EventEmitter<SelectedGridData>();
  @Output() public editInlineClicked: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public deleteClicked: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public saveClicked: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public editDocumentClicked: EventEmitter<Event> = new EventEmitter<Event>();
  protected readonly LinkedCollectionStorageType = LinkedCollectionStorageType;

  public onOpenClicked(): void {
    const field = this.gridFieldSignal();
    if (field) {
      this.openClicked.emit({
        viewDataSourceId: field.DataDesignViewDataSourcesID,
        crossLinkedInstancesId: this.record().RowDataDesignCrossID ?? this.record().CrossLinkedInstancesID
      });
    }
  }

  public onEditInNewWindowClicked(): void {
    const field = this.gridFieldSignal();
    if (field) {
      this.editInNewWindowClicked.emit({
        viewDataSourceId: field.DataDesignViewDataSourcesID,
        crossLinkedInstancesId: this.record().RowDataDesignCrossID ?? this.record().CrossLinkedInstancesID
      });
    }
  }

  private externalUserCheck(combineWith?: boolean): boolean {
    if ((this.userType != UserType.AnonymousUser && this.userType != UserType.ExternalUser) || (this.formType != null && this.formType != FormType.ExternalForm)) {
      return true;
    }

    if (this.externalAccess == undefined) {
      return false;
    }

    return combineWith ?? true;
  }
}

export interface SelectedGridData {
  viewDataSourceId: number;
  crossLinkedInstancesId: number;
}